import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BellRing, CloudDownload, FileDown, FileUp, FolderCheck, Loader2, Lock, Users } from 'lucide-react';
import { haccpApi, erroreHaccp, scaricaPdf } from '../haccpApi';
import { BadgeStato, quando } from './stati';
import { BadgeFirmato, BottoneFirma, VoceDocumento } from './VoceDocumento';
import { nuovaFirma, useFirme } from './firme/useFirme';
import { FirmeCard } from './firme/FirmeCard';
import { FirmeDialog } from './firme/FirmeDialog';
import { FirmaSpazio } from './firme/FirmaSpazio';
import { CaricaDocumenti } from './CaricaDocumenti';
import { VediDocumento } from './VediDocumento';
import { ModificaDocumento } from './ModificaDocumento';
import { PersoneDialog } from './PersoneDialog';
import { ImportaHaccpOk } from './ImportaHaccpOk';
import { useDpi } from './dpi/useDpi';
import { VerbaliDpiCard } from './dpi/VerbaliDpiCard';
import { VerbaliDpiDialog } from './dpi/VerbaliDpiDialog';

const DA_SISTEMARE = ['scaduto', 'mancante', 'in_scadenza'];

const Riepilogo = ({ r, filtro, setFiltro }) => {
  const chip = [['sistemare', `${r.scaduto + r.mancante + r.in_scadenza} da sistemare`, 'bg-red-50 border-red-200 text-red-800'],
    ['valido', `${r.valido} validi`, 'bg-emerald-50 border-emerald-200 text-emerald-800'], ['da_valutare', `${r.da_valutare} da valutare`, 'bg-slate-50 border-slate-200 text-slate-700']];
  return (
    <div className="flex flex-wrap gap-2">
      {chip.map(([k, testo, stile]) => (
        <button key={k} onClick={() => setFiltro(filtro === k ? null : k)} data-testid={`documenti-filtro-${k}`}
          className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${stile} ${filtro === k ? 'ring-2 ring-gray-900' : ''}`}>{testo}</button>
      ))}
    </div>
  );
};

const Altri = ({ altri, onVedi, onModifica, onFirma, onAggiungiFirma }) => (!altri.length ? null : (
  <div className="space-y-2">
    <h4 className="text-sm font-bold text-gray-900">Altri documenti</h4>
    {altri.map((d) => (
      <div key={d.id} className="rounded-2xl border border-gray-200 bg-white p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`documenti-altro-${d.id}`}>
        <div className="min-w-0"><p className="text-sm font-semibold text-gray-900">{d.titolo}</p><p className="text-[11px] text-gray-500">{quando(d, d.giorni)}</p>
          {d.note?.startsWith('Persona non riconosciuta') && <p className="mt-1 inline-flex px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold" data-testid={`documenti-altro-${d.id}-scegli-persona`}>Scegli di chi è: apri Modifica</p>}</div>
        <div className="flex flex-wrap gap-1.5 items-center"><BadgeStato stato={d.stato} /><BadgeFirmato doc={d} testid={`documenti-altro-${d.id}`} />
          <BottoneFirma doc={d} onFirma={onFirma} onAggiungiFirma={onAggiungiFirma} titolo={d.titolo} testid={`documenti-altro-${d.id}`} />
          <button onClick={() => onVedi(d, d.titolo)} className="h-9 px-3 rounded-lg border border-gray-300 bg-white text-xs text-gray-700" data-testid={`documenti-altro-${d.id}-vedi`}>Vedi</button>
          <button onClick={() => onModifica(d, d.titolo)} className="h-9 px-3 rounded-lg border border-gray-300 bg-white text-xs text-gray-700" data-testid={`documenti-altro-${d.id}-modifica`}>Modifica</button>
        </div>
      </div>
    ))}
  </div>
));

export const DocumentiSection = () => {
  const [responsabile, setResponsabile] = useState(null);
  const [dati, setDati] = useState(null);
  const [categoria, setCategoria] = useState('tutte');
  const [filtro, setFiltro] = useState(null);
  const [carica, setCarica] = useState(null);
  const [vedi, setVedi] = useState(null);
  const [modifica, setModifica] = useState(null);
  const [persone, setPersone] = useState(false);
  const [pdf, setPdf] = useState(false);
  const [importa, setImporta] = useState(false);
  const [firmeAperto, setFirmeAperto] = useState(null);
  const [aggiungi, setAggiungi] = useState(null);
  const aggiungiFirma = (doc, titolo) => setAggiungi(nuovaFirma(doc, titolo));
  const firme = useFirme(!!responsabile);
  const [dpiAperto, setDpiAperto] = useState(false);
  const dpi = useDpi(!!responsabile, dpiAperto);
  const firmaDpi = (id) => { setDpiAperto(false); firme.ricarica(); setFirmeAperto(id); };

  const ricarica = useCallback(async () => {
    try { setDati(await haccpApi.get('/documenti')); } catch (e) { toast.error(erroreHaccp(e, 'Documenti non caricati')); }
  }, []);
  useEffect(() => {
    haccpApi.get('/documenti/permessi').then((p) => { setResponsabile(p.responsabile); if (p.responsabile) ricarica(); }).catch(() => setResponsabile(false));
  }, [ricarica]);

  const voci = useMemo(() => (dati?.voci || []).filter((v) => (categoria === 'tutte' || v.categoria === categoria)
    && (!filtro || (filtro === 'sistemare' ? DA_SISTEMARE.includes(v.stato) : v.stato === filtro))), [dati, categoria, filtro]);
  const chiudiVedi = useCallback(() => setVedi(null), []);
  const aggiornaTutto = () => { ricarica(); firme.ricarica(); dpi.ricarica(); };
  const ricontrollaFirme = async () => {
    try { await haccpApi.post('/documenti/firme/verifica', {}); toast.success('Ricontrollo le firme di tutti i documenti'); firme.ricarica(); }
    catch (e) { toast.error(erroreHaccp(e, 'Ricontrollo non avviato')); }
  };

  const nonApplicabile = async (codice, valore) => {
    try { await haccpApi.post('/documenti/non-applicabile', { codice, non_applicabile: valore }); ricarica(); }
    catch (e) { toast.error(erroreHaccp(e, 'Non aggiornato')); }
  };
  const cartella = async () => {
    setPdf(true);
    try { await scaricaPdf('/documenti/cartella/pdf', 'Documenti_attivita.pdf'); } catch (e) { toast.error(erroreHaccp(e, 'PDF non disponibile')); } finally { setPdf(false); }
  };

  if (responsabile === null || (responsabile && !dati)) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!responsabile) {
    return <div className="p-5"><div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-3" data-testid="documenti-solo-responsabile"><Lock className="h-5 w-5 text-gray-500" /><p className="text-sm text-gray-700">I documenti li vede e li carica solo il responsabile.</p></div></div>;
  }
  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="documenti-section">
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-sky-700 flex items-center justify-center shrink-0"><FolderCheck className="h-6 w-6 text-white" /></div>
          <div><h3 className="font-bold text-gray-900">Documenti in regola</h3>
            <p className="text-sm text-gray-600">Tutto quello che serve a un ristorante, con scadenze e attestati del personale.</p></div>
        </div>
        <Riepilogo r={dati.riepilogo} filtro={filtro} setFiltro={setFiltro} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <button onClick={() => setCarica({ titolo: 'Carica documenti' })} data-testid="documenti-carica-btn" className="h-12 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold flex items-center justify-center gap-2 transition-colors"><FileUp className="h-5 w-5" />Carica documenti</button>
          <button onClick={() => setImporta(true)} data-testid="documenti-haccpok-btn" className="h-12 rounded-full border-2 border-sky-700 bg-white text-sky-800 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-sky-50 transition-colors"><CloudDownload className="h-4 w-4" />Importa da HaccpOK</button>
          <button onClick={cartella} disabled={pdf} data-testid="documenti-cartella-pdf" className="h-12 rounded-full bg-gray-900 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">{pdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}Cartella per l'ispettore</button>
          <button onClick={() => setPersone(true)} data-testid="documenti-persone-btn" className="h-12 rounded-full border border-gray-300 bg-white text-sm font-semibold text-gray-800 flex items-center justify-center gap-2"><Users className="h-4 w-4" />Persone ({dati.persone.filter((p) => p.attivo).length})</button>
        </div>
        <p className="flex items-center gap-1.5 text-[11px] text-gray-500"><BellRing className="h-3.5 w-3.5" />Ti avviso 30 giorni prima, 7 giorni prima e il giorno della scadenza.</p>
      </div>
      <FirmeCard dati={firme.dati} onApri={() => setFirmeAperto('tutti')} onRicontrolla={ricontrollaFirme} />
      <VerbaliDpiCard dati={dpi.dati} onApri={() => setDpiAperto(true)} />
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {[['tutte', 'Tutti'], ...Object.entries(dati.categorie)].map(([k, n]) => (
          <button key={k} onClick={() => setCategoria(k)} data-testid={`documenti-categoria-${k}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${categoria === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'}`}>{n}</button>
        ))}
      </div>
      <div className="space-y-2.5">
        {voci.map((v) => <VoceDocumento key={v.codice} v={v} livelli={dati.livelli} onCarica={setCarica} onVedi={(doc, t) => setVedi({ doc, t })}
          onModifica={(doc, t) => setModifica({ doc, t })} onNonApplicabile={nonApplicabile} onFirma={setFirmeAperto} onAggiungiFirma={aggiungiFirma} onEliminato={aggiornaTutto} onDpi={() => setDpiAperto(true)} />)}
        {!voci.length && <p className="text-sm text-gray-600 text-center" data-testid="documenti-nessuno">Niente da mostrare con questo filtro.</p>}
      </div>
      {(categoria === 'tutte' && !filtro) && <Altri altri={dati.altri} onVedi={(doc, t) => setVedi({ doc, t })} onModifica={(doc, t) => setModifica({ doc, t })} onFirma={setFirmeAperto} onAggiungiFirma={aggiungiFirma} />}
      <CaricaDocumenti preset={carica} catalogo={dati.voci} persone={dati.persone} onClose={() => setCarica(null)} onSalvato={aggiornaTutto} />
      <VediDocumento documento={vedi?.doc} titolo={vedi?.t} onClose={chiudiVedi} />
      <ModificaDocumento documento={modifica?.doc} titolo={modifica?.t} conStorico={dati.voci.find((v) => v.codice === modifica?.doc?.tipo)?.con_storico} voci={dati.voci} persone={dati.persone}
        onClose={() => setModifica(null)} onSalvato={aggiornaTutto} />
      <PersoneDialog open={persone} persone={dati.persone} onClose={() => setPersone(false)} onCambiato={ricarica} />
      <ImportaHaccpOk open={importa} onClose={() => setImporta(false)} onImportato={aggiornaTutto} />
      <FirmeDialog aperto={!!firmeAperto} soloDoc={firmeAperto !== 'tutti' ? firmeAperto : null} dati={firme.dati} onClose={() => setFirmeAperto(null)} onCambiato={aggiornaTutto} />
      <FirmaSpazio aperto={aggiungi} dati={firme.dati} onClose={() => setAggiungi(null)} onFirmato={aggiornaTutto} />
      <VerbaliDpiDialog aperto={dpiAperto} dpi={dpi} onClose={() => setDpiAperto(false)} onFirma={firmaDpi} onCambiato={aggiornaTutto} />
    </div>
  );
};

export default DocumentiSection;
